import { computed, effect, inject } from '@angular/core';
import {
  getState,
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  AgentProfileId,
  ContactInfosDto,
  CurrencyDto,
  EmailDto,
  IdentityVerificationStatus,
  LanguageDto,
  MobileNumberDto,
  OwnerProfileId,
  TimezoneDto,
  UserAvatarFileDto,
  UserDto,
  UserEmailNotificationsDto,
  UserIdentityDto,
  UserRole,
} from '@workspace/data-types';
import { ApplicationStore } from '../application/application.store';
import { withStoreDevtools } from '../with-devtools';

export interface UserState {
  current: UserDto | null;
  deactivated: boolean;
  deleted: boolean;
  loading: boolean;
  error?: string;
}

const initialState: UserState = {
  current: null,
  deleted: false,
  deactivated: false,
  loading: false,
  error: undefined,
};

export const UserStore = signalStore(
  { providedIn: 'root' },
  withStoreDevtools('user'),
  withState(initialState),

  withMethods((store) => {
    return {
      removeCurrentUser() {
        patchState(store, { current: null });
      },

      updateCurrentUser(user: UserDto) {
        patchState(store, {
          current: user,
          deleted: false,
          deactivated: false,
        });
      },

      updateCurrentUserAgentProfile(agentProfileId: AgentProfileId) {
        if (!store.current()) return;
        const current = store.current() as UserDto;
        patchState(store, {
          current: {
            ...current,
            profiles: {
              ...current.profiles,
              agent: agentProfileId,
            },
          },
        });
      },

      updateCurrentUserOwnerProfile(ownerProfileId: OwnerProfileId) {
        if (!store.current()) return;
        const current = store.current() as UserDto;
        patchState(store, {
          current: {
            ...current,
            profiles: {
              ...current.profiles,
              owner: ownerProfileId,
            },
          },
        });
      },

      updateCurrentUserIdentity(userIdentity: UserIdentityDto) {
        if (!store.current()) return;

        const current = store.current() as UserDto;

        patchState(store, {
          current: {
            ...current,
            identity: {
              ...current.identity,
              ...userIdentity,
            },
          },
        });
      },

      updateCurrentUserIdentityVerificationStatus(verificationStatus: IdentityVerificationStatus) {
        if (!store.current()) return;

        const current = store.current() as UserDto;

        patchState(store, {
          current: {
            ...current,
            identity: {
              ...current.identity,
              verificationStatus,
            },
          },
        });
      },

      updateCurrentUserCredentialsTimestamp() {
        if (!store.current()) return;

        const current = store.current() as UserDto;

        patchState(store, {
          current: {
            ...current,
            metadata: {
              ...current.metadata,
              credentials: { lastUpdateAt: new Date() },
            },
          },
        });
      },

      updateCurrentUserIdentityAvatar(avatar: UserAvatarFileDto | undefined) {
        if (!store.current()) return;

        const current = store.current() as UserDto;
        patchState(store, {
          current: {
            ...current,
            identity: {
              ...current.identity,
              avatar: avatar ? { ...avatar } : undefined,
            },
          },
        });
      },

      updateCurrentUserContactPhoneNumber(mobileNumber: MobileNumberDto) {
        const user: UserDto = store.current() as UserDto;
        if (!store.current()) return;

        patchState(store, {
          current: {
            ...user,
            contactInfos: { ...user.contactInfos, ...mobileNumber },
          },
        });
      },

      updateCurrentUserContactEmail(email: EmailDto) {
        const user: UserDto = store.current() as UserDto;
        if (!store.current()) return;

        patchState(store, {
          current: {
            ...user,
            contactInfos: { ...user.contactInfos, ...email },
          },
        });
      },

      updateCurrentUserLanguage(language: LanguageDto) {
        const user: UserDto = store.current() as UserDto;
        if (!store.current()) return;

        patchState(store, {
          current: {
            ...user,
            settings: {
              ...user.settings,
              internationalization: {
                ...user.settings.internationalization,
                ...language,
              },
            },
          },
        });
      },

      updateCurrentUserTimezone(timezone: TimezoneDto) {
        const user: UserDto = store.current() as UserDto;
        if (!store.current()) return;

        patchState(store, {
          current: {
            ...user,
            settings: {
              ...user.settings,
              internationalization: {
                ...user.settings.internationalization,
                ...timezone,
              },
            },
          },
        });
      },

      updateCurrentUserCurrency(currency: CurrencyDto) {
        const user: UserDto = store.current() as UserDto;
        if (!store.current()) return;

        patchState(store, {
          current: {
            ...user,
            settings: {
              ...user.settings,
              internationalization: {
                ...user.settings.internationalization,
                ...currency,
              },
            },
          },
        });
      },

      updateCurrentUserEmailNotifications(emailNotifications: UserEmailNotificationsDto) {
        const user: UserDto = store.current() as UserDto;
        if (!store.current()) return;

        patchState(store, {
          current: {
            ...user,
            settings: {
              ...user.settings,
              notifications: {
                ...user.settings?.notifications,
                email: {
                  ...user.settings?.notifications?.email,
                  ...emailNotifications,
                },
              },
            },
          },
        });
      },

      deleteUser() {
        patchState(store, {
          current: undefined,
          deleted: true,
          loading: false,
        });
      },

      deactivateUser() {
        patchState(store, {
          current: undefined,
          deactivated: true,
          loading: false,
        });
      },

      updateCurrentUserContactInfos(contactInfos: ContactInfosDto) {
        if (!store.current()) return;
        patchState(store, {
          current: { ...(store.current() as UserDto), contactInfos },
        });
      },
    };
  }),

  withComputed((store) => ({
    currentUser: computed(() => store.current()),
    deactivatedUser: computed(() => store.deactivated()),
    deletedUser: computed(() => store.deleted()),
    isLoading: computed(() => store.loading()),
    hasError: computed(() => !!store.error?.()),
    isAdmin: computed(() => store.current()?.roles?.includes(UserRole.ADMIN) ?? false),
    isSubscribed: computed(() => {
      const status = store.current()?.subscription?.status;
      return status === 'active' || status === 'trialing';
    }),
    hasBeenSubscribed: computed(() => {
      return !!store.current()?.subscription?.status || !!store.current()?.subscription?._id;
    }),
    hasOwnerProfile: computed(() => !!store.current()?.profiles?.owner),
    isOwner: computed(() => !!store.current()?.profiles?.owner),
    hasAgentProfile: computed(() => !!store.current()?.profiles?.agent),
    isAgent: computed(() => !!store.current()?.profiles?.agent),
  })),

  withHooks((store) => {
    const applicationStore = inject(ApplicationStore);

    return {
      onInit() {
        effect(() => {
          if (applicationStore.debug()) {
            console.debug('User state:', getState(store));
          }
        });
      },
    };
  }),
);
